/**
 * WEB-3956 юнит 8 — раздел маршрутов «проект»: каркас кабинета под входом
 * (`ProtectedRoute` → `DashboardLayout`) и его страницы-дети уровня проекта.
 * Страницы организации — соседний раздел `org.tsx`, они идут детьми того же
 * корня следом; собирает `router/index.tsx`.
 */
import { Navigate, type RouteObject } from 'react-router-dom';
import { ProtectedRoute } from '../ProtectedRoute';
import { DashboardLayout } from '../DashboardLayout';
import { DraftRoute } from '../DraftRoute';
import { AnalyticsLeadsRedirect } from '../AnalyticsLeadsRedirect';
import LandingRedirect from '../landingRoute';
import { lazyWithRetry } from '@/utils/lazyWithRetry';
import { CabinetSuspense } from '../suspense';

const QuizEditorPage = lazyWithRetry(
  () => import('@/pages/QuizEditor'),
  'page:QuizEditor',
);
const PaywallEditorPage = lazyWithRetry(
  () => import('@/pages/PaywallEditor'),
  'page:PaywallEditor',
);
// WEB-3764: холст воронки — одна карта, рамки «Квиз» и «Пейволл».
const FunnelCanvasPage = lazyWithRetry(
  () => import('@/pages/FunnelCanvas'),
  'page:FunnelCanvas',
);
const DomainsPage = lazyWithRetry(
  () => import('@/pages/Domains'),
  'page:Domains',
);
const EmailSequencesPage = lazyWithRetry(
  () => import('@/pages/EmailSequences'),
  'page:EmailSequences',
);
const EmailSequenceDetailPage = lazyWithRetry(
  () => import('@/pages/EmailSequenceDetail'),
  'page:EmailSequenceDetail',
);
const SettingsPage = lazyWithRetry(
  () => import('@/pages/Settings'),
  'page:Settings',
);
// WEB-342: Project-level settings page (split from Settings.tsx)
const ProjectSettingsPage = lazyWithRetry(
  () => import('@/pages/ProjectSettings'),
  'page:ProjectSettings',
);
// WEB-665 FE-audit: единый экран «Аналитика» по канону analytics-mockup;
// старый /statistics ведёт на /analytics. WEB-4035: лидов в аналитике нет —
// /leads и /analytics?tab=leads ведут в «Подписки» → «Лиды».
const AnalyticsPage = lazyWithRetry(
  () => import('@/pages/Analytics'),
  'page:Analytics',
);
// WEB-3748 юнит 2: главная проекта (дашборд). Посадка «/» пока прежняя —
// её переключает юнит 3, когда на странице будут шаги воронки и график.
const ProjectHomePage = lazyWithRetry(
  () => import('@/pages/ProjectHome'),
  'page:ProjectHome',
);
const FunnelGenerationPage = lazyWithRetry(
  () => import('@/pages/FunnelGeneration'),
  'page:FunnelGeneration',
);
// WEB-2187: страниц возврата покупателя после оплаты (`/handoff/*`) в
// кабинетном роутере НЕТ. Они живут в публичном входе сборки
// (`router/publicRouter.tsx`, страницы `pages/PublicHandoff*`): на
// `/handoff/success` платёжная система уводит настоящего покупателя, а
// кабинетная сборка несёт с собой запись экрана (session-replay, WEB-469) —
// на странице, где видны его почта, имя и код доступа. Раздатчик отдаёт
// `/handoff/*` публичную оболочку на ЛЮБОМ хосте (`frontend/Caddyfile`), так
// что второй двери здесь быть не должно.
const CommercePage = lazyWithRetry(
  () => import('@/pages/Commerce'),
  'page:Commerce',
);
// WEB-1084: страница покупателя («Магазин» → «Клиенты» → карточка человека) —
// новый уровень навигации поверх плоского списка клиентов.
const CommerceCustomerDetailPage = lazyWithRetry(
  () => import('@/pages/CommerceCustomerDetail'),
  'page:CommerceCustomerDetail',
);
// PM 2026-07-25: «Аудит» — самостоятельный раздел бокового меню на двух
// уровнях: /org/audit (журналы организации) и /audit (журналы проекта).
// Раньше жил под-вкладкой в личных Настройках → «Организация».
// WEB-3956 юнит 8: одна ленивая страница на оба уровня — раздел `org.tsx`
// берёт её отсюда, а не заводит вторую: та же страница при переходе
// /audit ↔ /org/audit остаётся тем же компонентом, как было в общем файле.
export const AuditPage = lazyWithRetry(() => import('@/pages/Audit'), 'page:Audit');
// AI-студия (канон design/ai-studio-mockup): единый раздел ИИ-функций.
// В боковом меню его НЕТ по решению PM — входы из таблиц контента и
// из конструктора.
const AiStudioPage = lazyWithRetry(
  () => import('@/pages/AiStudio'),
  'page:AiStudio',
);
// WEB-647 A3.1 (FULLSWEEP «Контент»): единый раздел объединяет
// Воронки/Квизы/Пейволлы/Линки под одним PageHeader + Tabs (канон
// content-tables-mockup/src/ContentTablesPage.tsx). Старые роуты ниже
// редиректят на /content?tab=... — internal navigate('/funnels') и т.п. и
// закладки продолжают работать.
const ContentPage = lazyWithRetry(
  () => import('@/pages/Content'),
  'page:Content',
);
// FE-audit «Контент» (2026-07-17): Линки выведены из раздела «Контент»
// (макет content-tables-mockup теперь 3 вкладки: Воронки/Квизы/Пейволлы,
// smartlinks переехал в свой мокап). A/B-тесты живут отдельным разделом
// /smart-links; переверстка страницы по smartlinks-mockup — следующий шаг.
const SmartLinksPage = lazyWithRetry(
  () => import('@/pages/SmartLinks'),
  'page:SmartLinks',
);
// WEB-3750 юнит 2: страница одного A/B-теста (вкладки «Обзор» / «Результаты»).
const SmartLinkTestPage = lazyWithRetry(
  () => import('@/pages/SmartLinkTest'),
  'page:SmartLinkTest',
);

/** Корень кабинета: каркас под входом. */
export const CABINET_ROOT_ELEMENT = (
  <ProtectedRoute>
    <DashboardLayout />
  </ProtectedRoute>
);

/** Страницы-дети `DashboardLayout` уровня проекта (порядок — прежний). */
export const PROJECT_ROUTES: RouteObject[] = [
  {
    // WEB-407 открывал орг-дашборд всегда. WEB-820 (отзыв PM 2026-07-25):
    // если активный проект выбран — открываем проект, иначе орг-дашборд.
    // Меню считается по адресу (Sidebar, WEB-438), поэтому согласованность
    // «где я» и «что в меню» обеспечивается именно здесь.
    index: true,
    element: <LandingRedirect />,
  },
  {
    // WEB-3748 юнит 2: главная проекта; без активного проекта ведёт туда же,
    // куда посадка (LandingRedirect).
    path: 'home',
    element: <CabinetSuspense><ProjectHomePage /></CabinetSuspense>,
  },
  {
    // WEB-647 A3.1: единый раздел «Контент» (Воронки/Квизы/Пейволлы/Линки).
    path: 'content',
    element: <CabinetSuspense><ContentPage /></CabinetSuspense>,
  },
  {
    // Старый список воронок — редирект на объединённый раздел, вкладка
    // по умолчанию (Tabs сама открывает 'funnels' без ?tab=).
    path: 'funnels',
    element: <Navigate to="/content" replace />,
  },
  {
    path: 'quizzes',
    element: <Navigate to="/content?tab=quizzes" replace />,
  },
  {
    path: 'quizzes/generate/:generationId',
    element: <CabinetSuspense><FunnelGenerationPage kind="funnel" /></CabinetSuspense>,
  },
  {
    path: 'paywalls/generate/:generationId',
    element: <CabinetSuspense><FunnelGenerationPage kind="paywall" /></CabinetSuspense>,
  },
  {
    path: 'funnels/:id/edit',
    element: <CabinetSuspense><FunnelCanvasPage /></CabinetSuspense>,
  },
  {
    path: 'quizzes/:id/edit',
    element: <CabinetSuspense><QuizEditorPage /></CabinetSuspense>,
  },
  {
    path: 'paywalls',
    element: <Navigate to="/content?tab=paywalls" replace />,
  },
  {
    path: 'paywalls/:id/edit',
    element: <CabinetSuspense><PaywallEditorPage /></CabinetSuspense>,
  },
  {
    path: 'leads',
    // WEB-4035 (владелец 29.10): поимённый список лидов — «Подписки» → «Лиды».
    element: <Navigate to="/commerce?tab=leads" replace />,
  },
  {
    path: 'analytics',
    // WEB-4035: старая ссылка ?tab=leads — туда же, а не на «Воронку».
    element: (
      <AnalyticsLeadsRedirect>
        <CabinetSuspense><AnalyticsPage /></CabinetSuspense>
      </AnalyticsLeadsRedirect>
    ),
  },
  {
    // WEB-407: /dashboard раньше давал «Not found» — редиректим на орг-дашборд.
    path: 'dashboard',
    element: <Navigate to="/org/dashboard" replace />,
  },
  {
    // WEB-385 → WEB-661: standalone A/B Experiments deleted entirely
    // (superseded by smart links — spec _smartlink.md §5). Bare redirect
    // kept ONLY for stale bookmarks/old links; no experiments code remains.
    // WEB-4091: straight to /analytics (was /statistics → /analytics, two hops).
    path: 'experiments',
    element: <Navigate to="/analytics" replace />,
  },
  {
    path: 'domains',
    element: <CabinetSuspense><DomainsPage /></CabinetSuspense>,
  },
  // WEB-3768 (владелец 25.10 п. 79, 27.10): раздел писем на бою скрыт —
  // без флага черновика адреса уводят на главную, код страниц на месте.
  {
    path: 'email-sequences',
    element: (
      <DraftRoute>
        <CabinetSuspense>
          <EmailSequencesPage />
        </CabinetSuspense>
      </DraftRoute>
    ),
  },
  {
    path: 'email-sequences/:id',
    element: (
      <DraftRoute>
        <CabinetSuspense>
          <EmailSequenceDetailPage />
        </CabinetSuspense>
      </DraftRoute>
    ),
  },
  {
    path: 'settings',
    element: <CabinetSuspense><SettingsPage /></CabinetSuspense>,
  },
  // WEB-342: Project-level settings (General/Stripe/Integrations/Members/Invites/Context)
  {
    path: 'project/settings',
    element: <CabinetSuspense><ProjectSettingsPage /></CabinetSuspense>,
  },
  {
    // Старая «Статистика» — теперь вкладка по умолчанию единой «Аналитики»
    // (Tabs сама открывает 'stats' без ?tab=).
    path: 'statistics',
    element: <Navigate to="/analytics" replace />,
  },
  {
    // FE-audit «Контент»: раздел выведен из «Контента» в свой адрес.
    // WEB-2013 (строка 7): раздел называется «A/B-тесты» (WEB-1026), и
    // адрес обязан звать его так же — иначе человек копирует из строки
    // браузера ссылку на раздел, которого в интерфейсе нет.
    path: 'ab-tests',
    element: <CabinetSuspense><SmartLinksPage /></CabinetSuspense>,
  },
  {
    // WEB-3750 юнит 2: страница теста; :tab (необязательный) — overview | results
    // (чужое значение страница читает как «Обзор»).
    path: 'ab-tests/:id/:tab?',
    element: <CabinetSuspense><SmartLinkTestPage /></CabinetSuspense>,
  },
  {
    // Прежний адрес раздела — перенаправление ради закладок и старых
    // ссылок, тем же приёмом, что '/funnels' → '/content' выше.
    path: 'smart-links',
    element: <Navigate to="/ab-tests" replace />,
  },
  {
    path: 'commerce',
    element: <CabinetSuspense><CommercePage /></CabinetSuspense>,
  },
  {
    path: 'commerce/customers/:email',
    element: <CabinetSuspense><CommerceCustomerDetailPage /></CabinetSuspense>,
  },
  {
    path: 'ai-studio',
    element: <CabinetSuspense><AiStudioPage /></CabinetSuspense>,
  },
  {
    // Аудит проекта: журнал команды проекта + трафик этого проекта.
    path: 'audit',
    element: <CabinetSuspense><AuditPage scope="project" /></CabinetSuspense>,
  },
];
